/*
 * The admin API's fetch plumbing: the base URL, the bearer header, and the one error envelope
 * (`{ error: { code, message } }`, api.md conventions) unwrapped into a discriminated result a
 * screen can branch on without try/catch.
 *
 * Extracted when the visibility surfaces (CAE-269) would have made the seventh copy of it —
 * contests-client, config-client and the catalogue clients each hand-rolled their own. Those three
 * still do: migrating them is a mechanical change worth its own PR, not a rider on this one.
 */

export interface ApiFailure {
  readonly ok: false;
  readonly status: number;
  readonly code: string | undefined;
  readonly message?: string;
  /** Present on the guard failures that carry structured context (e.g. GL-16's finalise blocks).
   * Untyped by the generated ErrorResponse schema, which declares only code/message — read
   * defensively. */
  readonly details?: Record<string, unknown>;
}

export type ApiResult<T> = ({ ok: true } & T) | ApiFailure;
export type ApiBodyResult<T> = { ok: true; body: T } | ApiFailure;

/**
 * Query values a client passes through; `undefined` and empty entries are dropped rather than
 * sent as `?x=`. Declared as a readonly index signature so a client's own typed filter interface
 * (`ListPlayersFilter`, …) satisfies it without restating its keys.
 */
export interface Query {
  readonly [key: string]: string | number | undefined;
}

function apiBase(): string {
  return import.meta.env.VITE_API_BASE_URL ?? '';
}

function authHeaders(adminToken: string): HeadersInit {
  return { 'content-type': 'application/json', authorization: `Bearer ${adminToken}` };
}

async function failure(response: Response): Promise<ApiFailure> {
  const body = (await response.json().catch(() => null)) as {
    error?: { code?: string; message?: string; details?: Record<string, unknown> };
  } | null;
  return {
    ok: false,
    status: response.status,
    code: body?.error?.code,
    message: body?.error?.message,
    details: body?.error?.details,
  };
}

function queryString(query: Query | undefined): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const encoded = params.toString();
  return encoded ? `?${encoded}` : '';
}

export const adminApi = {
  /** GET, spreading the body into the result — for envelope responses (`{ users, nextCursor }`). */
  async get<T>(adminToken: string, path: string, query?: Query): Promise<ApiResult<T>> {
    const response = await fetch(`${apiBase()}${path}${queryString(query)}`, {
      method: 'GET',
      headers: authHeaders(adminToken),
    });
    if (response.ok) {
      return { ok: true, ...((await response.json()) as T) };
    }
    return failure(response);
  },

  /** POST, returning the body under `body` — for responses that are themselves a resource. */
  async post<T>(adminToken: string, path: string, body: unknown = {}): Promise<ApiBodyResult<T>> {
    const response = await fetch(`${apiBase()}${path}`, {
      method: 'POST',
      headers: authHeaders(adminToken),
      body: JSON.stringify(body),
    });
    if (response.ok) {
      return { ok: true, body: (await response.json()) as T };
    }
    return failure(response);
  },

  /** PUT, returning the body under `body` — for a mutation whose response is the updated resource. */
  async put<T>(adminToken: string, path: string, body: unknown = {}): Promise<ApiBodyResult<T>> {
    const response = await fetch(`${apiBase()}${path}`, {
      method: 'PUT',
      headers: authHeaders(adminToken),
      body: JSON.stringify(body),
    });
    if (response.ok) {
      return { ok: true, body: (await response.json()) as T };
    }
    return failure(response);
  },
};
